import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useI18n } from "../i18n";
import { useUi } from "../lib/ui";
import { smoothScrollToId, smoothScrollToTop } from "../lib/scroll";

const SECTIONS = [
  { id: "sectores", key: "nav.sectors" },
  { id: "circuito", key: "nav.process" },
  { id: "transparencia", key: "nav.transparency" },
  { id: "panel", key: "nav.data" },
  { id: "regiones", key: "nav.regions" },
  { id: "contacto", key: "nav.contact" },
];

export default function Footer() {
  const { t, lang, path } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestCookiePrefs } = useUi();
  const isHome = location.pathname === `/${lang}` || location.pathname === `/${lang}/`;

  const goToSection = (id) => {
    if (isHome) {
      smoothScrollToId(id);
    } else {
      navigate(path(""), { state: { scrollTo: id } });
    }
  };

  const legalLines = t("footer.legalLines") || [];
  const offices = t("footer.offices") || [];

  return (
    <footer className="site-footer" id="footer">
      <div className="container footer-grid">
        <div className="footer-brand-col">
          <Link
            to={path("")}
            className="footer-brand"
            onClick={(e) => {
              if (isHome) {
                e.preventDefault();
                smoothScrollToTop();
              }
            }}
            data-testid="footer-logo"
          >
            <Logo variant="light" size={34} />
            <span className="footer-brand-text">
              <span className="brand-name">{t("brand.name")}</span>
              <span className="brand-tagline">{t("brand.domain")}</span>
            </span>
          </Link>
          <p className="footer-about">{t("footer.about")}</p>
          <div className="footer-offices">
            {offices.map((o) => (
              <div className="footer-office" key={o.city}>
                <span className="footer-office-city">{o.city}</span>
                <span className="footer-office-address">{o.address}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="footer-col">
          <h3 className="footer-title">{t("footer.navTitle")}</h3>
          <ul className="footer-list">
            <li>
              <Link
                to={path("")}
                className="footer-link"
                onClick={(e) => {
                  if (isHome) {
                    e.preventDefault();
                    smoothScrollToTop();
                  }
                }}
                data-testid="footer-home"
              >
                {t("nav.home")}
              </Link>
            </li>
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className="footer-link"
                  onClick={() => goToSection(s.id)}
                  data-testid={`footer-${s.id}`}
                >
                  {t(s.key)}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="footer-col">
          <h3 className="footer-title">{t("footer.legalTitle")}</h3>
          <ul className="footer-list">
            <li>
              <Link to={path("/privacy")} className="footer-link" data-testid="footer-privacy">
                {t("nav.privacy")}
              </Link>
            </li>
            <li>
              <Link to={path("/cookies")} className="footer-link" data-testid="footer-cookies">
                {t("nav.cookies")}
              </Link>
            </li>
            <li>
              <Link to={path("/terms")} className="footer-link" data-testid="footer-terms">
                {t("nav.terms")}
              </Link>
            </li>
            <li>
              <button
                type="button"
                className="footer-link"
                onClick={requestCookiePrefs}
                data-testid="footer-cookie-settings"
              >
                {t("footer.cookieSettings")}
              </button>
            </li>
          </ul>
        </div>

        <div className="footer-col">
          <h3 className="footer-title">{t("footer.contactTitle")}</h3>
          <ul className="footer-list">
            <li>
              <a className="footer-link" href="mailto:consultas@recursosarg.com" data-testid="footer-email">
                consultas@recursosarg.com
              </a>
            </li>
            <li>
              <a className="footer-link" href="mailto:legal@recursosarg.com" data-testid="footer-legal-email">
                legal@recursosarg.com
              </a>
            </li>
            <li>
              <a className="footer-link" href="tel:+541143182900" data-testid="footer-phone">
                +54 11 4318-2900
              </a>
            </li>
            <li>
              <button
                type="button"
                className="footer-link"
                onClick={() => goToSection("contacto")}
                data-testid="footer-form-link"
              >
                {t("common.writeUs")}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="container footer-legal">
        <h3 className="footer-title">{t("footer.companyTitle")}</h3>
        <ul className="footer-legal-list">
          {legalLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p className="footer-disclaimer">{t("footer.disclaimer")}</p>
      </div>

      <div className="footer-bottom">
        <div className="container footer-bottom-inner">
          <span>
            &copy; 2019&ndash;2026 {t("footer.companyName")} {t("footer.rights")}
          </span>
          <span className="footer-bottom-links">
            <Link to={path("/privacy")} className="footer-link">
              {t("nav.privacy")}
            </Link>
            <Link to={path("/cookies")} className="footer-link">
              {t("nav.cookies")}
            </Link>
            <Link to={path("/terms")} className="footer-link">
              {t("nav.terms")}
            </Link>
          </span>
        </div>
      </div>
    </footer>
  );
}
